import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchDomCoupon } from '../store/dom-coupon/actions';
import { createDomCouponSelectors } from '../store/dom-coupon/selectors';
import type { CouponResponse, DomCouponState } from '@travel/traveler-core/store/dom-coupon/types';
import useIsInViewport from './useIsInViewport';

type UseFetchCouponOnViewParams = {
  providerId: number;
  requestString: string;
  enabled?: boolean; // Allow disabling the hook
  isEncoded?: boolean; // Whether the requestString is already URL-encoded (default: true)
};

type UseFetchCouponOnViewReturn = {
  couponData: CouponResponse | null;
  isFetching: boolean;
  error: any;
  ref: React.MutableRefObject<HTMLDivElement | null>;
  isInViewport: boolean;
};

// Define a minimal state shape for this hook
type HookState = {
  coupon: DomCouponState;
};

// Create selectors for this app's state shape
const couponSelectors = createDomCouponSelectors<HookState>((state) => state.coupon);

/**
 * Hook to fetch coupon data when the component is in the viewport
 * Reusable across different applications
 *
 * @param providerId - The hotel/provider ID
 * @param requestString - The coupon API request string (JSON serialized parameters)
 * @param enabled - Whether the hook is enabled (default: true)
 * @param isEncoded - Whether the requestString is already URL-encoded (default: true)
 * @returns Object containing coupon data, fetching state, error, ref, and viewport visibility
 */
const useFetchCouponOnView = ({
  providerId,
  requestString,
  enabled = true,
  isEncoded = true,
}: UseFetchCouponOnViewParams): UseFetchCouponOnViewReturn => {
  const dispatch = useDispatch();
  const ref = useRef<HTMLDivElement>(null);
  const isInViewport = useIsInViewport(ref);
  const [hasFetched, setHasFetched] = useState(false);

  // Decode the request string if it's already encoded to prevent double encoding
  const decodedRequestString =
    isEncoded && requestString ? decodeURIComponent(requestString) : requestString;

  // Get coupon data from store
  const couponData = useSelector(couponSelectors.getData(providerId));
  const isFetching = useSelector(couponSelectors.getIsFetching(providerId));
  const error = useSelector(couponSelectors.getError(providerId));

  useEffect(() => {
    // Only fetch once when the component enters the viewport
    if (enabled && isInViewport && !hasFetched && !isFetching && !couponData) {
      dispatch(
        fetchDomCoupon({
          providerId,
          requestString: decodedRequestString,
        }) as any,
      );
      setHasFetched(true);
    }
  }, [
    enabled,
    isInViewport,
    hasFetched,
    isFetching,
    couponData,
    dispatch,
    providerId,
    decodedRequestString,
  ]);

  return {
    couponData,
    isFetching,
    error,
    ref,
    isInViewport,
  };
};

export default useFetchCouponOnView;
